import { HttpStatusCode } from "axios";
import { getApiErrorData, getApiErrorStatus } from "_shared/_errors/api-error-message";
import { Masked } from "work/masked-fields";
import type ProposalBuilder from "work/proposals/__builder/proposal-builder";
import type WorkDocument from "work/work-agreements/contracts/work-document";

/** Where a proposal save's 403 lists the documents the saver may not read. Mirrors the API's own key. */
export const REFUSED_DOCUMENT_IDS_FIELD = "refusedDocumentIds";

/**
 * The sentence for a save refused because it names documents the saver may not read, naming them.
 *
 * A draft started from a previous proposal names that proposal's documents without the user having
 * picked any of them (#1569), so "one or more selected documents" left them nothing to act on. The
 * server lists the refused ids; the names come from the draft, which is where the user will look.
 * Answers undefined for any other failure, so the caller's own message stands.
 */
export function documentRefusalMessage(error: unknown, builder: ProposalBuilder | null | undefined): string | undefined {
  if (getApiErrorStatus(error) !== HttpStatusCode.Forbidden) return undefined;
  const refusedIds = refusedDocumentIdsOf(getApiErrorData(error));
  if (refusedIds.length === 0) return undefined;

  const documents = documentsOf(builder);
  const names = refusedIds.map(
    (id) => documents.find((document) => document.id.value.toLowerCase() === id.toLowerCase())?.name.value,
  );
  const named = names.filter((name): name is string => Boolean(name));
  const unnamedCount = names.length - named.length;
  const subjects = [
    ...named.map((name) => `"${name}"`),
    ...(unnamedCount > 0 ? [`${unnamedCount} other ${unnamedCount === 1 ? "document" : "documents"}`] : []),
  ];
  const plural = refusedIds.length > 1;
  return (
    `You do not have access to ${listed(subjects)}, so ${plural ? "they" : "it"} cannot be added to this ` +
    `proposal. Remove ${plural ? "them" : "it"} and save again.`
  );
}

function refusedDocumentIdsOf(data: unknown): string[] {
  if (typeof data !== "object" || data === null || !("errors" in data)) return [];
  const errors = data.errors;
  if (typeof errors !== "object" || errors === null || !(REFUSED_DOCUMENT_IDS_FIELD in errors)) return [];
  const ids = (errors as Record<string, unknown>)[REFUSED_DOCUMENT_IDS_FIELD];
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
}

function documentsOf(builder: ProposalBuilder | null | undefined): WorkDocument[] {
  const workAgreement = builder?.currentSpec.workAgreement;
  if (!workAgreement) return [];
  return [workAgreement.conflicts, workAgreement.clientPolicies, workAgreement.vendorPolicies].flatMap(
    (documents) => (documents && Masked.isNotMasked(documents) ? documents : []),
  );
}

function listed(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}
